import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Check } from 'lucide-react';

/* ─── DATA ─── */
const STEPS = [
  {
    id: 'type',
    label: 'Type de projet',
    subtitle: 'Quel type de solution recherchez-vous ?',
    options: [
      { value: 'site-vitrine', label: 'Site vitrine' },
      { value: 'application-web', label: 'Application web' },
      { value: 'plateforme-saas', label: 'Plateforme SaaS' },
      { value: 'automatisation-ia', label: 'Automatisation / IA' },
      { value: 'projet-complexe', label: 'Projet complexe / sur-mesure' },
    ],
    multi: false,
  },
  {
    id: 'complexity',
    label: 'Niveau de complexité',
    subtitle: 'Quelle est l\'envergure technique envisagée ?',
    options: [
      { value: 'simple', label: 'Simple' },
      { value: 'intermediaire', label: 'Intermédiaire' },
      { value: 'avance', label: 'Avancé' },
      { value: 'tres-complexe', label: 'Très complexe' },
    ],
    multi: false,
  },
  {
    id: 'features',
    label: 'Fonctionnalités',
    subtitle: 'Sélectionnez les éléments qui concernent votre projet.',
    options: [
      { value: 'auth', label: 'Authentification utilisateurs' },
      { value: 'dashboard', label: 'Dashboard' },
      { value: 'paiement', label: 'Paiement en ligne' },
      { value: 'api-externe', label: 'API externe' },
      { value: 'ia-auto', label: 'IA / automatisation' },
      { value: 'realtime', label: 'Temps réel / WebSocket' },
      { value: 'responsive', label: 'Mobile responsive avancé' },
      { value: 'admin', label: 'Admin panel' },
      { value: 'multi-users', label: 'Multi-utilisateurs / rôles' },
      { value: 'infra', label: 'Infrastructure scalable' },
    ],
    multi: true,
  },
  {
    id: 'scope',
    label: 'Envergure',
    subtitle: 'À quelle phase en êtes-vous ?',
    options: [
      { value: 'mvp', label: 'MVP' },
      { value: 'produit-complet', label: 'Produit complet' },
      { value: 'plateforme-scalable', label: 'Plateforme scalable' },
      { value: 'infra-complexe', label: 'Infrastructure complexe' },
    ],
    multi: false,
  },
  {
    id: 'budget',
    label: 'Budget cible',
    subtitle: 'Optionnel mais recommandé pour affiner l\'estimation.',
    options: [
      { value: '<2000', label: '< 2 000€' },
      { value: '2000-5000', label: '2 000€ – 5 000€' },
      { value: '5000-15000', label: '5 000€ – 15 000€' },
      { value: '15000-50000', label: '15 000€ – 50 000€' },
      { value: '50000+', label: '50 000€+' },
    ],
    multi: false,
    optional: true,
  },
];

/* ─── ESTIMATION LOGIC ─── */
const TYPE_BASE = {
  'site-vitrine': [800, 3000],
  'application-web': [3000, 15000],
  'plateforme-saas': [8000, 40000],
  'automatisation-ia': [1500, 10000],
  'projet-complexe': [15000, 80000],
};

const COMPLEXITY_MULT = {
  simple: [1, 1],
  intermediaire: [1.3, 1.5],
  avance: [2, 2.5],
  'tres-complexe': [3, 4],
};

const SCOPE_MULT = {
  mvp: [0.6, 0.7],
  'produit-complet': [1, 1],
  'plateforme-scalable': [1.6, 1.8],
  'infra-complexe': [2.5, 3],
};

const FEATURE_ADD = {
  auth: 500,
  dashboard: 1500,
  paiement: 800,
  'api-externe': 600,
  'ia-auto': 2000,
  realtime: 1500,
  responsive: 400,
  admin: 1200,
  'multi-users': 1000,
  infra: 3000,
};

function computeEstimate(answers) {
  const base = TYPE_BASE[answers.type] || [2000, 10000];
  const cMult = COMPLEXITY_MULT[answers.complexity] || [1, 1];
  const sMult = SCOPE_MULT[answers.scope] || [1, 1];
  const featCost = (answers.features || []).reduce((s, f) => s + (FEATURE_ADD[f] || 0), 0);

  let low = base[0] * cMult[0] * sMult[0] + featCost * 0.7;
  let high = base[1] * cMult[1] * sMult[1] + featCost * 1.3;

  // Round to nice numbers
  low = Math.round(low / 500) * 500;
  high = Math.round(high / 1000) * 1000;

  if (low < 800) low = 800;
  if (high <= low) high = low + 2000;

  return { low, high };
}

function formatPrice(n) {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1).replace('.0', '')}M€`;
  return n.toLocaleString('fr-FR') + '€';
}

/* ─── COMPONENTS ─── */

const OptionButton = ({ label, selected, onClick, multi }) => (
  <motion.button
    type="button"
    onClick={onClick}
    className={`relative text-left px-5 py-4 rounded-lg border transition-colors duration-300 interactive ${
      selected
        ? 'border-[#2A3B8F] bg-[#2A3B8F]/10 text-white'
        : 'border-[#1a1a1a] bg-transparent text-[#999] hover:border-[#333] hover:text-white'
    }`}
    whileTap={{ scale: 0.98 }}
  >
    <span className="text-sm">{label}</span>
    {selected && (
      <motion.div
        className="absolute top-3 right-3"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 500, damping: 25 }}
      >
        <Check size={14} className="text-[#2A3B8F]" />
      </motion.div>
    )}
  </motion.button>
);

const StepIndicator = ({ current, total }) => (
  <div className="flex items-center gap-2 mb-12">
    {Array.from({ length: total }).map((_, i) => (
      <div
        key={i}
        className={`h-0.5 flex-1 rounded-full transition-colors duration-500 ${
          i <= current ? 'bg-[#2A3B8F]' : 'bg-[#1a1a1a]'
        }`}
      />
    ))}
  </div>
);

const slideVariants = {
  enter: (dir) => ({ x: dir > 0 ? 60 : -60, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? -60 : 60, opacity: 0 }),
};

/* ─── MAIN ─── */
const ProjectEstimator = () => {
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [answers, setAnswers] = useState({ features: [] });
  const [showResult, setShowResult] = useState(false);
  const containerRef = useRef(null);

  const navigate = useNavigate();
  const currentStep = STEPS[step];

  const selectOption = (value) => {
    if (currentStep.multi) {
      setAnswers((prev) => {
        const list = prev[currentStep.id] || [];
        return {
          ...prev,
          [currentStep.id]: list.includes(value)
            ? list.filter((v) => v !== value)
            : [...list, value],
        };
      });
    } else {
      setAnswers((prev) => ({ ...prev, [currentStep.id]: value }));
    }
  };

  const isSelected = (value) => {
    if (currentStep.multi) return (answers[currentStep.id] || []).includes(value);
    return answers[currentStep.id] === value;
  };

  const canProceed = currentStep.optional || (
    currentStep.multi
      ? (answers[currentStep.id] || []).length > 0
      : !!answers[currentStep.id]
  );

  const next = () => {
    if (step < STEPS.length - 1) {
      setDirection(1);
      setStep((s) => s + 1);
    } else {
      setShowResult(true);
    }
  };

  const prev = () => {
    if (showResult) {
      setShowResult(false);
    } else if (step > 0) {
      setDirection(-1);
      setStep((s) => s - 1);
    }
  };

  const estimate = computeEstimate(answers);

  const goToContact = () => {
    const typeLabel = STEPS[0].options.find((o) => o.value === answers.type)?.label || '';
    const complexLabel = STEPS[1].options.find((o) => o.value === answers.complexity)?.label || '';
    const featLabels = (answers.features || [])
      .map((f) => STEPS[2].options.find((o) => o.value === f)?.label)
      .filter(Boolean)
      .join(', ');

    const message = `Type de projet : ${typeLabel}\nComplexité : ${complexLabel}\nFonctionnalités : ${featLabels || 'Aucune spécifiée'}\nEstimation : ${formatPrice(estimate.low)} – ${formatPrice(estimate.high)}`;

    navigate('/#contact', { state: { prefill: { project_type: typeLabel, message } } });
  };

  return (
    <section className="px-[5%] pb-32" data-testid="project-estimator">
      <div className="max-w-2xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-[#2A3B8F] uppercase tracking-widest text-sm mb-4">
            Estimation
          </p>
          <h2 className="text-white text-2xl sm:text-3xl font-semibold mb-3">
            Estimez votre projet
          </h2>
          <p className="text-[#666] text-sm">
            5 questions pour cadrer votre besoin.
          </p>
        </motion.div>

        <div
          ref={containerRef}
          className="border border-[#1a1a1a] rounded-2xl p-6 sm:p-10 min-h-[420px] flex flex-col"
        >
          <AnimatePresence mode="wait" custom={direction}>
            {!showResult ? (
              <motion.div
                key={step}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="flex-1 flex flex-col"
              >
                <StepIndicator current={step} total={STEPS.length} />

                <p className="text-[#555] text-xs uppercase tracking-widest mb-2">
                  {currentStep.label}
                  {currentStep.optional && <span className="text-[#333] ml-1">(optionnel)</span>}
                </p>
                <p className="text-white text-base sm:text-lg mb-8">
                  {currentStep.subtitle}
                </p>

                <div className={`grid gap-3 mb-auto ${
                  currentStep.multi ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'
                }`}>
                  {currentStep.options.map((opt) => (
                    <OptionButton
                      key={opt.value}
                      label={opt.label}
                      selected={isSelected(opt.value)}
                      onClick={() => selectOption(opt.value)}
                      multi={currentStep.multi}
                    />
                  ))}
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="result"
                custom={1}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="flex-1 flex flex-col items-center justify-center text-center py-6"
                data-testid="estimator-result"
              >
                <motion.p
                  className="text-[#2A3B8F] uppercase tracking-widest text-xs mb-8"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  Estimation de votre projet
                </motion.p>

                <motion.div
                  className="mb-8"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                >
                  <span className="text-white text-3xl sm:text-4xl font-bold">
                    {formatPrice(estimate.low)}
                  </span>
                  <span className="text-[#444] text-2xl sm:text-3xl font-light mx-3">—</span>
                  <span className="text-white text-3xl sm:text-4xl font-bold">
                    {formatPrice(estimate.high)}
                  </span>
                </motion.div>

                <motion.p
                  className="text-[#888] text-sm leading-relaxed max-w-md mb-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  Ce type de projet nécessite une approche sur-mesure, orientée performance et scalabilité.
                </motion.p>

                <motion.p
                  className="text-[#555] text-xs mb-10"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                >
                  Estimation indicative — un devis précis suit un échange rapide.
                </motion.p>

                <motion.button
                  onClick={goToContact}
                  className="interactive inline-flex items-center gap-2 px-8 py-4 bg-[#2A3B8F] text-white font-medium tracking-wide rounded-lg hover:bg-[#3A4BA0] transition-colors duration-200"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                  data-testid="estimator-cta"
                >
                  <span>Recevoir une estimation détaillée</span>
                  <ArrowRight size={18} />
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Navigation buttons */}
          <div className="flex justify-between items-center mt-8 pt-6 border-t border-[#111]">
            <button
              onClick={prev}
              disabled={step === 0 && !showResult}
              className="interactive flex items-center gap-2 text-[#555] text-sm hover:text-white transition-colors disabled:opacity-20 disabled:cursor-default"
              data-testid="estimator-prev"
            >
              <ArrowLeft size={16} />
              <span>Retour</span>
            </button>

            {!showResult && (
              <div className="flex items-center gap-4">
                {currentStep.optional && !answers[currentStep.id] && (
                  <button
                    onClick={next}
                    className="interactive text-[#555] text-sm hover:text-white transition-colors"
                  >
                    Passer
                  </button>
                )}
                <button
                  onClick={next}
                  disabled={!canProceed}
                  className="interactive flex items-center gap-2 text-white text-sm bg-[#2A3B8F] px-5 py-2.5 rounded-lg hover:bg-[#3A4BA0] transition-colors disabled:opacity-30 disabled:cursor-default"
                  data-testid="estimator-next"
                >
                  <span>{step === STEPS.length - 1 ? 'Voir l\'estimation' : 'Continuer'}</span>
                  <ArrowRight size={16} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProjectEstimator;
